import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Mail, Phone, MapPin, Hash, Calendar, FileText, Edit3, Save,
  X, Shield, ToggleLeft, ToggleRight, Users, GitBranch, TestTube2, BookOpen,
  Plus, Trash2, ExternalLink, CheckCircle2, Clock, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const TABS = [
  { key: 'info', label: 'معلومات', icon: Building2 },
  { key: 'subscription', label: 'الاشتراك', icon: Shield },
  { key: 'branches', label: 'الفروع', icon: GitBranch },
  { key: 'zatca', label: 'ZATCA', icon: FileText },
  { key: 'usage', label: 'الاستخدام', icon: TestTube2 },
];

const statusBadge = (status) => {
  const map = {
    active: { label: 'نشط', cls: 'bg-emerald-100 text-emerald-700' },
    trial: { label: 'تجريبي', cls: 'bg-amber-100 text-amber-700' },
    expired: { label: 'منتهي', cls: 'bg-red-100 text-red-700' },
    suspended: { label: 'موقوف', cls: 'bg-red-100 text-red-700' },
    pending: { label: 'معلق', cls: 'bg-yellow-100 text-yellow-700' },
    completed: { label: 'مكتمل', cls: 'bg-green-100 text-green-700' },
  };
  const s = map[status] || { label: status || '-', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const TenantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ key: '', value: '' });

  useEffect(() => {
    fetchTenant();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'usage' && !usage) {
      fetchUsage();
    }
  }, [activeTab]);

  const fetchTenant = async () => {
    try {
      const response = await superAdminAPI.getTenant(id);
      const raw = response.data?.data || response.data;
      // Backend returns { tenant, branches, subscription, ... } — flatten for easier access
      const tenantData = raw.tenant ? { ...raw.tenant, branches: raw.branches || [], subscription: raw.subscription || null, userCount: raw.userCount, zatcaStatus: raw.zatcaStatus } : raw;
      setTenant(tenantData);
      setEditData({
        name: tenantData.name || '',
        email: tenantData.email || '',
        phone: tenantData.phoneNumber || '',
        vatNumber: tenantData.vatNumber || '',
        address: typeof tenantData.address === 'object' ? (tenantData.address?.street || '') : (tenantData.address || ''),
        slug: tenantData.slug || '',
        adminNotes: tenantData.adminNotes || '',
      });
    } catch (error) {
      toast.error('فشل في تحميل بيانات المختبر');
      navigate('/admin/tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    setLoadingUsage(true);
    try {
      const response = await superAdminAPI.getTenantUsage(id);
      setUsage(response.data?.data || response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await superAdminAPI.updateTenant(id, editData);
      toast.success('تم تحديث بيانات المختبر');
      setEditing(false);
      fetchTenant();
    } catch (error) {
      toast.error('فشل في تحديث البيانات');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await superAdminAPI.toggleTenantStatus(id);
      toast.success('تم تحديث حالة المختبر');
      fetchTenant();
    } catch (error) {
      toast.error('فشل في تحديث الحالة');
    }
  };

  const handleImpersonate = async () => {
    try {
      const response = await superAdminAPI.impersonate(id);
      const data = response.data?.data || response.data;
      if (data.token) {
        // Store impersonation token and redirect
        localStorage.setItem('token', data.token);
        localStorage.setItem('impersonating', 'true');
        window.open('/', '_blank');
      }
      toast.success('تم الدخول كمختبر');
    } catch (error) {
      toast.error('فشل في الدخول كمختبر');
    }
  };

  const handleAddOverride = async () => {
    if (!overrideForm.key) return;
    try {
      const subId = tenant.subscription?._id || tenant.subscriptionId;
      await superAdminAPI.setFeatureOverride(subId, {
        featureKey: overrideForm.key,
        value: overrideForm.value === 'true' ? true : overrideForm.value === 'false' ? false : overrideForm.value,
      });
      toast.success('تم إضافة التخصيص');
      setOverrideForm({ key: '', value: '' });
      fetchTenant();
    } catch (error) {
      toast.error('فشل في إضافة التخصيص');
    }
  };

  const handleRemoveOverride = async (key) => {
    try {
      const subId = tenant.subscription?._id || tenant.subscriptionId;
      await superAdminAPI.removeFeatureOverride(subId, key);
      toast.success('تم حذف التخصيص');
      fetchTenant();
    } catch (error) {
      toast.error('فشل في حذف التخصيص');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="bg-white rounded-2xl p-6 animate-pulse space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-5 bg-gray-200 rounded w-full max-w-md" />
          ))}
        </div>
      </div>
    );
  }

  if (!tenant) return null;

  const subscription = tenant.subscription || {};
  const branches = tenant.branches || [];
  const overrides = subscription.featureOverrides || tenant.featureOverrides || {};

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{tenant.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            {statusBadge(tenant.subscription?.status || (tenant.isActive ? 'active' : 'suspended'))}
            <span className="text-sm text-gray-500">{tenant.email}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleImpersonate}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            الدخول كمختبر
          </button>
          <button
            onClick={handleToggleStatus}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              tenant.isActive
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {tenant.isActive ? (
              <><ToggleRight className="w-4 h-4" /> إيقاف</>
            ) : (
              <><ToggleLeft className="w-4 h-4" /> تفعيل</>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-gray-100 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* INFO TAB */}
        {activeTab === 'info' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">معلومات المختبر</h2>
              {editing ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" /> حفظ
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                  >
                    <X className="w-4 h-4" /> إلغاء
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                >
                  <Edit3 className="w-4 h-4" /> تعديل
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'اسم المختبر', key: 'name', icon: Building2 },
                { label: 'البريد الإلكتروني', key: 'email', icon: Mail },
                { label: 'رقم الجوال', key: 'phone', icon: Phone },
                { label: 'الرقم الضريبي', key: 'vatNumber', icon: Hash },
                { label: 'العنوان', key: 'address', icon: MapPin },
                { label: 'الرابط (Slug)', key: 'slug', icon: ExternalLink },
              ].map((field) => (
                <div key={field.key} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                  <field.icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">{field.label}</p>
                    {editing ? (
                      <input
                        type="text"
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-sm font-medium text-gray-800">
                        {typeof tenant[field.key] === 'object' ? JSON.stringify(tenant[field.key]) : (tenant[field.key] || '-')}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Admin Notes (full width) */}
              <div className="md:col-span-2 flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">ملاحظات الأدمن</p>
                  {editing ? (
                    <textarea
                      value={editData.adminNotes || ''}
                      onChange={(e) => setEditData({ ...editData, adminNotes: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm text-gray-800">{tenant.adminNotes || 'لا توجد ملاحظات'}</p>
                  )}
                </div>
              </div>

              {/* Created At */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">تاريخ التسجيل</p>
                  <p className="text-sm font-medium text-gray-800">
                    {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUBSCRIPTION TAB */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">تفاصيل الاشتراك</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-blue-50">
                  <p className="text-xs text-blue-600 mb-1">الباقة</p>
                  <p className="text-lg font-bold text-blue-800">{subscription.planId?.nameAr || subscription.planId?.name || '-'}</p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">الحالة</p>
                  <div className="mt-1">{statusBadge(subscription.status || 'inactive')}</div>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">الدورة</p>
                  <p className="text-sm font-medium text-gray-800">
                    {subscription.billingCycle === 'yearly' ? 'سنوي' : 'شهري'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">تاريخ البداية</p>
                  <p className="text-sm font-medium text-gray-800">
                    {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">تاريخ الانتهاء</p>
                  <p className="text-sm font-medium text-gray-800">
                    {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">السعر</p>
                  <p className="text-sm font-medium text-gray-800">
                    {subscription.totalPrice != null ? `${subscription.totalPrice} ر.س` : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Overrides */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">تخصيصات الميزات</h2>

              {Object.keys(overrides).length > 0 ? (
                <div className="space-y-2 mb-4">
                  {Object.entries(overrides).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div>
                        <span className="text-sm font-medium text-gray-800">{key}</span>
                        <span className="text-sm text-gray-500 mr-2">= {String(value)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveOverride(key)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-4">لا توجد تخصيصات</p>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="اسم الميزة"
                  value={overrideForm.key}
                  onChange={(e) => setOverrideForm({ ...overrideForm, key: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="القيمة"
                  value={overrideForm.value}
                  onChange={(e) => setOverrideForm({ ...overrideForm, value: e.target.value })}
                  className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddOverride}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BRANCHES TAB */}
        {activeTab === 'branches' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">الفروع</h2>
            </div>
            {branches.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {branches.map((branch, i) => (
                  <div key={branch._id || i} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{branch.name}</p>
                      <p className="text-xs text-gray-500">{branch.address || branch.city || '-'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {branch.zatcaStatus && statusBadge(branch.zatcaStatus)}
                      {branch.isMain && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">رئيسي</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400 text-sm">
                <GitBranch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                لا توجد فروع
              </div>
            )}
          </div>
        )}

        {/* ZATCA TAB */}
        {activeTab === 'zatca' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">حالة ZATCA</h2>
            </div>
            {branches.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {branches.map((branch, i) => (
                  <div key={branch._id || i} className="px-6 py-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-800">{branch.name}</p>
                      {statusBadge(branch.zatcaStatus || 'pending')}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'CSR', done: branch.zatca?.csrGenerated },
                        { label: 'CSID', done: branch.zatca?.complianceCsid },
                        { label: 'Production CSID', done: branch.zatca?.productionCsid },
                        { label: 'Reporting', done: branch.zatca?.reportingEnabled },
                      ].map((step, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs">
                          {step.done ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-300" />
                          )}
                          <span className={step.done ? 'text-green-700' : 'text-gray-400'}>{step.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400 text-sm">
                لا توجد فروع لعرض حالة ZATCA
              </div>
            )}
          </div>
        )}

        {/* USAGE TAB */}
        {activeTab === 'usage' && (
          <div>
            {loadingUsage ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-4 w-20 bg-gray-200 rounded mb-3" />
                    <div className="h-8 w-12 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    <p className="text-sm text-gray-500">الحجوزات</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{usage?.bookings ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-green-500" />
                    <p className="text-sm text-gray-500">المستخدمين</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{usage?.users ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <GitBranch className="w-5 h-5 text-purple-500" />
                    <p className="text-sm text-gray-500">الفروع</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{usage?.branches ?? 0}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <TestTube2 className="w-5 h-5 text-amber-500" />
                    <p className="text-sm text-gray-500">الفحوصات</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{usage?.tests ?? 0}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TenantDetails;
